import React from 'react';

import { Button, Group, LoadingOverlay, Modal, Stack, Text, Title } from '@mantine/core';
import useClass from 'hooks/class/useClass';
import useDeleteClass from 'hooks/class/useDeleteClass';
import notificationHandler from 'utils/notifications';

interface IModalClassDeleteProps {
  opened: boolean;
  handleCloseModal: () => void;
  handleCloseDrawer: () => void;
  kiloClassId?: string;
  setKiloClassId: (kiloClassId: string | undefined) => void;
}

const ModalClassDelete = ({
  opened,
  kiloClassId,
  handleCloseModal,
  handleCloseDrawer,
  setKiloClassId,
}: IModalClassDeleteProps): JSX.Element => {
  const deleteClass = useDeleteClass();
  const { data, isLoading } = useClass(kiloClassId);

  const { successNotification, errorNotification } = notificationHandler();
  const handleDelete = (): void => {
    if (data) {
      deleteClass.mutate(data.data.kiloClassId, {
        onSuccess: () => {
          setKiloClassId(undefined);
          successNotification('Class delete success', `This class has been successfully deleted.`);
          handleCloseModal();
          handleCloseDrawer();
        },
        onError: error => {
          errorNotification('Class delete failed', error.response?.data.message);
        },
      });
    }
  };
  return (
    <Modal
      centered
      trapFocus
      opened={opened}
      onClose={handleCloseModal}
      title={<Title order={4}>Delete Class</Title>}
      size='auto'
      data-cy='deleteClassModal'
    >
      <LoadingOverlay visible={isLoading} />
      <Stack>
        <Text>{`Are you sure you want to delete ${data?.data.kiloClassType.name}?`}</Text>
        {data?.data.athleteKiloClasses.some(akc => akc.isLateCancel || akc.isNoShow) && (
          <Text color='orange'>
            {`Important: ${
              data.data.athleteKiloClasses.some(akc => akc.isLateCancel) &&
              data.data.athleteKiloClasses.some(akc => akc.isNoShow)
                ? 'Late cancellation and no-show fees apply'
                : data.data.athleteKiloClasses.some(akc => akc.isLateCancel)
                ? 'A late cancellation fee applies'
                : 'A no-show fee applies'
            }. An invoice will still be generated and linked to this class after deletion.`}
          </Text>
        )}
        <Text color='dimmed'>This action cannot be undone.</Text>
        <Group position='right'>
          <Button
            onClick={handleDelete}
            color='red'
            loading={deleteClass.isLoading}
            disabled={isLoading}
            data-cy='deleteClassBtn'
          >
            Delete
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default ModalClassDelete;
